import { Auth } from 'aws-amplify';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';

import AccountLinkedImg from '@/assets/oauthsvgs/account-linked-sso-email.svg';
import EmailVerifiedImg from '@/assets/oauthsvgs/email-is-verified.svg';
import ForgotPasswordImg from '@/assets/oauthsvgs/forgot-password-mail-sent.svg';
import NeedlLogo from '@/assets/oauthsvgs/needl-logo.svg';
import OauthErrorImg from '@/assets/oauthsvgs/oauth-error.svg';
import ResetConfirmedImg from '@/assets/oauthsvgs/password-changed.svg';
import { Card } from '@/atoms/AuthV2/Card';
import { CardHeader } from '@/components/AuthV2/CardHeader';
import { Button } from '@/components/Button/Button';
import { useQueryParams } from '@/hooks/useQueryParams';
import { AUTH_ROUTES, OAUTH_ROUTES } from '@/nav-routes';

const OAuthCallbackHandler = (): JSX.Element => {
  const history = useHistory();
  const location = useLocation();
  const [, queryParams] = useQueryParams();

  let title = 'Error';
  let message = 'Something went wrong';
  let imgSrc = OauthErrorImg;
  let buttonText = 'Login';
  let showButton = true;
  let buttonLink = AUTH_ROUTES.SIGNIN;

  if (location.state && location.state.mode === 'sso-link') {
    title = 'Congratulations!';
    message =
      'Your SSO account has been linked to your existing Needl account. Log in to continue.';
    imgSrc = AccountLinkedImg;
    buttonText = 'Login';
  } else if (
    location.state &&
    location.state.mode === 'password-reset-successful'
  ) {
    title = 'Password Changed';
    message =
      'Your password has been updated successfully. Please login to your Needl account with the new password.';
    imgSrc = ResetConfirmedImg;
    buttonText = 'Login';
  } else if (location.state && location.state.mode === 'email-verified') {
    title = 'Your Email is Verified!';
    message = '';
    imgSrc = EmailVerifiedImg;
    buttonText = 'Login';
  } else if (
    location.state &&
    location.state.mode === 'awaiting-confirmation'
  ) {
    title = 'Verify Email';
    message =
      'Please click the verification link sent to your registered email ID.';
    imgSrc = ForgotPasswordImg;
    showButton = false;
  } else if (
    location.state &&
    location.state.mode === 'reset-password-confirmation-page'
  ) {
    title = 'Forgot password?';
    message =
      'If you are a registered user, you will receive an email with a reset link.';
    imgSrc = ForgotPasswordImg;
    buttonText = 'Login';
  } else if (location.state && location.state.mode === 'invalid-link') {
    title = 'Oops!';
    message = 'It appears that your link is either invalid or has expired.';
    imgSrc = OauthErrorImg;
    showButton = true;
    buttonText = 'Sign Up';
    buttonLink = AUTH_ROUTES.SIGNUP;
  } else if (
    location.state &&
    location.state.mode === 'invalid-link-resetpassword'
  ) {
    title = 'Invalid link';
    message = 'Your link is invalid or has expired.';
    imgSrc = OauthErrorImg;
    showButton = true;
    buttonText = 'Sign up';
    buttonLink = AUTH_ROUTES.SIGNUP;
  } else if (queryParams && queryParams.code && queryParams.email) {
    showButton = false;
    title = 'Just a moment';
    message = 'We are verifying if this is really you.';
    imgSrc = NeedlLogo;
  }

  React.useEffect(() => {
    async function verifySignup() {
      try {
        const signUpResponse = await Auth.confirmSignUp(
          queryParams.email as string,
          queryParams.code as string
        );
        if (signUpResponse === 'SUCCESS') {
          history.replace(OAUTH_ROUTES.REDIRECT, { mode: 'email-verified' });
        }
      } catch (e) {
        history.replace(OAUTH_ROUTES.REDIRECT, { mode: 'invalid-link' });
      }
    }
    if (queryParams.code && queryParams.email) {
      verifySignup();
    }
  }, [queryParams.email, queryParams.code, history]);

  return (
    <StyledCard>
      <CardHeader center title={title} text={message} />
      {imgSrc ? (
        <div className='w-1/2 aspect-square flex justify-center py-10'>
          <img className='w-full' src={imgSrc} alt='icon' />
        </div>
      ) : null}
      {showButton ? (
        <Button
          label={buttonText}
          size='medium'
          variant='primary'
          onClick={() => history.replace(buttonLink)}
        />
      ) : null}
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  ${tw`items-center`}
`;

export default OAuthCallbackHandler;
